import { Button, Flex, Group, Text, useMantineTheme } from "@mantine/core";
import { AlterAgreementRequests } from ".";
import {
  AlterationAgreementStatus,
  AlterationAgreementType,
  PaymentFrequency,
  useUpdateAlterAgreementRecordMutation,
} from "src/generated/graphql";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";
import useWritePermission from "src/hooks/usePermission";
interface IRequestDetailProps {
  item: AlterAgreementRequests;
  closeModal: () => void;
  onRefetch: () => void;
}
const RequestDetails = ({
  item,
  closeModal,
  onRefetch,
}: IRequestDetailProps) => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.locRequests);
  const {
    alterationAgreementType,
    alterationAgreementStatus,
    signedAgreement,
    createdDate,
    notes,
    startFreezeAt,
    intervals,
  } = item;
  const theme = useMantineTheme();

  const [alterAgreementRequest, { loading }] =
    useUpdateAlterAgreementRecordMutation({
      onCompleted: () => {
        onRefetch();
        closeModal();
      },
    });

  const handleSubmit = (status: AlterationAgreementStatus) => {
    alterAgreementRequest({
      variables: {
        id: item.id,
        data: {
          alterationAgreementStatus: status,
        },
      },
    });
  };

  const getDuration = (intervals: number, frequency: PaymentFrequency) => {
    switch (frequency) {
      case PaymentFrequency.Yearly:
        return `${intervals} Year${intervals > 1 && "s"}`;
      case PaymentFrequency.Monthly:
        return `${intervals} Month${intervals > 1 && "s"}`;
      case PaymentFrequency.BiWeekly:
        return `${intervals * 2} Weeks`;
      case PaymentFrequency.Weekly:
      default:
        return `${intervals} Week${intervals > 1 && "s"}`;
    }
  };

  return (
    <Flex direction={"column"} align={"flex-start"} justify={"center"}>
      <Group w="100%" position="apart">
        <Text fw={500}>Request Status : </Text>
        <Text>{alterationAgreementStatus}</Text>
      </Group>

      <Group w="100%" position="apart">
        <Text fw={500}>Requested Date : </Text>
        <Text>{new Date(createdDate).toDateString()}</Text>
      </Group>

      <Group w="100%" position="apart">
        <Text fw={500}>Client Name : </Text>
        <Text>{`${signedAgreement.client.user.firstName} ${signedAgreement.client.user.lastName}`}</Text>
      </Group>

      <Group w="100%" position="apart">
        <Text fw={500}>Request Type : </Text>
        <Text>{alterationAgreementType}</Text>
      </Group>

      <Group w="100%" position="apart">
        <Text fw={500}>Agreement : </Text>
        <Text>{signedAgreement.agreement.name}</Text>
      </Group>

      {alterationAgreementType == AlterationAgreementType.Freeze && (
        <Group w="100%" position="apart">
          <Text fw={500}>Freeze Date : </Text>
          <Text>{new Date(startFreezeAt).toDateString()}</Text>
        </Group>
      )}

      {alterationAgreementType == AlterationAgreementType.Freeze && (
        <Group w="100%" position="apart">
          <Text fw={500}>Duration : </Text>
          <Text>
            {getDuration(intervals, signedAgreement.agreement.paymentFrequency)}
          </Text>
        </Group>
      )}

      <Group w="100%" position="apart">
        <Text fw={500}>Reasoning : </Text>
        <Text>{notes}</Text>
      </Group>

      {isAllowWrite && (
        <Group position="apart" mt="md" w={"100%"} style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={() => handleSubmit(AlterationAgreementStatus.Denied)}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Deny Request
          </Button>
          <Button
            radius="md"
            onClick={() => handleSubmit(AlterationAgreementStatus.Approved)}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
          >
            Approve Request
          </Button>
        </Group>
      )}
    </Flex>
  );
};

export default RequestDetails;
